"use client"

import Link from "next/link"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { goKeys as k } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { GoogleRating } from "@/components/ui/google-rating"
import { HeroTrustStrip } from "@/components/ui/hero-trust-strip"
import { PartnerLogoMarquee } from "@/components/ui/partner-logo-marquee"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { FaqAccordion } from "@/components/ui/faq-accordion"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { DoctorsSection } from "../../../_components/doctors-section"
import { TestimonialsSection } from "../../../_components/testimonials-section"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { Testimonial } from "@/lib/cms"
import { SIGNS, FAQS } from "./female-hormones-data"
import { SignGlyph } from "./female-hormones-sign-glyphs"
import { SECTION } from "./female-hormones-components"
import HormoneReveal from "./female-hormones-reveal"
import { FemaleHormonesMriImmersive } from "./female-hormones-mri-immersive"
import {
  FemaleHormonesReport,
  FemaleHormonesMeasure,
  FemaleHormonesCoverage,
  FemaleHormonesProcess,
  FemaleHormonesGpCompare,
  FemaleHormonesPricing,
  FemaleHormonesClosingCta,
} from "./female-hormones-sections"

// Full-bleed hero photo (climbing wall), served from Vercel Blob.
const HERO_IMAGE = blobMediaUrl("/images/marketing/female-hormones-hero.png")

export default function FemaleHormonesClient({
  bloodDrawCount,
  experts,
  testimonials,
  showImaging,
}: {
  /** Live count of active blood-draw labs (CMS `locations`, type === "blood"). */
  bloodDrawCount: number
  experts: ReviewerExpert[]
  /** CMS testimonials for the reviews carousel (empty → section self-hides). */
  testimonials: Testimonial[]
  /**
   * Is this LP's imaging arm (full-body + pelvic MRI) actually sellable in the
   * visitor's market? Resolved once on the server (page.tsx) from the CMS
   * services control plane + the country's booking lifecycle. False → every
   * imaging surface is hidden rather than downgraded to a coming-soon/waitlist
   * block: an ad LP that advertises a product the market can't buy is a
   * conversion leak, not a teaser.
   */
  showImaging: boolean
}) {
  const t = useTranslations("go")
  const th = useTranslations("home")
  const lp = useLocalePath()

  return (
    <>
      {/* ── 1. Hero — full-bleed climbing photo with overlaid copy + bottom KPI
         strip (style ported from /go/immunity). Near-black fallback bg tuned to
         the photo so the load-in is seamless. ── */}
      {/* items-center on tall viewports; on short ones (laptops, split windows)
         top-align so the CTA is never centred below the fold / behind the
         bottom KPI strip — the page just scrolls to reveal it. */}
      <section className="relative isolate flex min-h-[calc(80vh+40px)] items-center overflow-hidden bg-neutral-900 text-white [@media(min-width:768px)_and_(max-height:850px)]:items-start">
        <Image
          src={HERO_IMAGE}
          alt="Woman bouldering on an indoor climbing wall"
          fill
          priority
          sizes="100vw"
          className="object-cover object-[80%_25%] md:object-[62%_28%]"
        />
        {/* top scrim for the transparent navbar */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-transparent"
          aria-hidden
        />
        {/* legibility scrim: bottom-up on mobile, left-to-right on desktop */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10 md:bg-gradient-to-r md:from-black/55 md:via-black/15 md:to-transparent"
          aria-hidden
        />

        <div className={`${SECTION} relative z-10 w-full`}>
          <div className="flex max-w-[636px] flex-col items-start gap-6 py-28 [@media(min-width:768px)_and_(max-height:850px)]:pt-24 [@media(min-width:768px)_and_(max-height:850px)]:pb-32">
            <p className="type-label-md text-white/60 uppercase">
              {t(k.femaleHormonesHeroEyebrow)}
            </p>
            <h1 className="type-display-md md:type-display-lg leading-[0.98] text-white">
              {t(k.femaleHormonesHeroHeading)}
            </h1>
            <p className="type-body-xl max-w-[480px] text-white/85">
              {t(k.femaleHormonesHeroBody)}
            </p>
            <GoogleRating
              variant="onDark"
              staticOnDark
              rating=""
              reviewLabel={t(k.femaleHormonesHeroReviewLabel)}
              className="self-start"
            />
            <BookingCta
              productId={[PRODUCT_IDS.bloodTest, PRODUCT_IDS.hormoneWomen]}
              step="addons"
              trackingLabel="female_hormones_hero_cta"
              trackingLocation="go/female-hormones"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {t(k.femaleHormonesHeroCta1)}
              </Button>
            </BookingCta>
          </div>
        </div>
        {/* bottom-pinned KPI strip — same component as the membership + immunity
           heroes; counts verified live against the CMS. */}
        <HeroTrustStrip
          align="left"
          compactMobile
          className="pb-4 md:pb-5"
          items={[
            {
              value: t(k.femaleHormonesHeroKpi1Value),
              label: t(k.femaleHormonesHeroKpi1Label),
            },
            {
              value: t(k.femaleHormonesHeroKpi2Value),
              label: t(k.femaleHormonesHeroKpi2Label),
            },
            {
              value: t(k.femaleHormonesHeroKpi3Value),
              label: t(k.femaleHormonesHeroKpi3Label),
            },
          ]}
        />
      </section>

      {/* ── 2. Partner logos — curated static row (metabolism treatment) ── */}
      <div className="mx-auto max-w-[1100px] px-6 pt-4 pb-6 md:px-10 md:pt-8">
        <PartnerLogoMarquee
          layout="static"
          only={[
            "Unilabs",
            "Oxford",
            "Google",
            "Siemens Healthineers",
            "TUM",
            "tech.eu",
          ]}
        />
      </div>

      {/* ── 3. Sound familiar? — the symptoms that point to a hormonal cause ── */}
      <section className="py-16 md:py-24">
        <div className={`${SECTION} flex flex-col items-center gap-12`}>
          {/* header — eyebrow + heading */}
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.femaleHormonesSignsEyebrow)}
            </p>
            <h2 className="type-display-md max-w-[600px] text-neutral-900">
              {t(k.femaleHormonesSignsHeading)}
            </h2>
          </div>

          {/* cards */}
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SIGNS.map((s) => (
              <div
                key={s.titleKey}
                className="flex flex-col items-center gap-4 rounded-[24px] bg-white p-10 text-center"
              >
                <span className="flex items-center justify-center text-neutral-900">
                  <SignGlyph name={s.glyph} />
                </span>
                <h3 className="type-title-lg max-w-[200px] text-neutral-900">
                  {t(s.titleKey)}
                </h3>
                <p className="type-body-xl text-neutral-500">{t(s.descKey)}</p>
              </div>
            ))}
          </div>

          {/* closing — wedge close (brain glyph, big heading, accent line) */}
          <div className="mt-16 mb-12 flex flex-col items-center gap-5 text-center md:mt-28 md:mb-24">
            <span className="text-neutral-900">
              <SignGlyph name="brain" />
            </span>
            <p className="type-display-md max-w-[760px] text-neutral-900">
              {t(k.femaleHormonesWedgeHeading1)}
              <span className="text-bright-blue-600 block">
                {t(k.femaleHormonesWedgeHeading2)}
              </span>
            </p>
            <p className="type-title-lg max-w-[820px] font-medium text-neutral-600">
              {t(k.femaleHormonesSignsBody)}
            </p>
          </div>
        </div>
      </section>

      {/* ── 5. What your hormones can actually tell you — immersive
          scroll-pinned reveal (blood stories + the pelvic-MRI structure tab,
          the latter only where the scan is sellable) ── */}
      <HormoneReveal showImaging={showImaging} />

      {/* ── 6. What we measure — the single measure block (four cross-panel
          cards: three add-on hormone groups + the base-test thyroid/metabolic
          markers). Replaces the old SystemsFigure + hormone-only explorer +
          health-risks trio. ── */}
      <FemaleHormonesMeasure />

      {/* ── 7b. An MRI sees what your blood can't — pinned scroll-reveal with
          the three structural findings (fibroids, ovarian cysts, deep
          endometriosis + adenomyosis). Before the coverage explorer, so the
          imaging arm comes ahead of the deep blood dive. Imaging markets only;
          hidden entirely where the scans aren't sellable. ── */}
      {showImaging && <FemaleHormonesMriImmersive />}

      {/* ── 7. Coverage explorer — live CMS-backed biomarker browser ── */}
      <FemaleHormonesCoverage />

      {/* ── 8. How it works — scroll-linked stepper (the optional MRI visit is
          imaging-gated; the remaining steps renumber by position) ── */}
      <FemaleHormonesProcess
        bloodDrawCount={bloodDrawCount}
        showImaging={showImaging}
      />

      {/* ── 9. Your report ── */}
      <FemaleHormonesReport />

      {/* ── 11. Testimonials carousel (CMS-backed; self-hides if none) ── */}
      <TestimonialsSection
        cmsTestimonials={testimonials}
        videoAspectClass="aspect-[4/5]"
      />

      {/* ── 12. Why not just my GP? — the last objection-handler before the offer ── */}
      <FemaleHormonesGpCompare showImaging={showImaging} />

      {/* ── 13. The offer — blood + hormone add-on vs the full check-up, or the
          membership fork / the standalone blood card where imaging isn't sold ── */}
      <FemaleHormonesPricing showImaging={showImaging} />

      {/* ── 14. Our expertise — shared DoctorsSection, homepage layout ── */}
      <DoctorsSection
        initialExperts={experts}
        showCount={false}
        columnHeading={
          <h2 className="type-display-md max-w-md text-neutral-900">
            {th(hk.expertsHeading)}
          </h2>
        }
      />

      {/* ── 15. FAQ — page-specific set, shared accordion ── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-6 md:grid-cols-[1fr_2fr] md:gap-12 md:px-10">
          <div>
            <h2 className="type-display-md text-neutral-900">
              {t(k.femaleHormonesFaqHeading)}
            </h2>
            <Link
              href={lp("/faq")}
              className="border-aubergine-800/30 text-aubergine-800 hover:bg-aubergine-800 hover:border-aubergine-800 type-title-sm mt-4 inline-flex items-center justify-center gap-1.5 rounded-full border px-5 py-2.5 transition-colors hover:text-white"
            >
              {th(hk.faqLink)} &rarr;
            </Link>
          </div>
          <div>
            <FaqAccordion
              items={FAQS.map((f) => ({
                question: t(f.qKey),
                answer: t(f.aKey),
              }))}
            />
          </div>
        </div>
      </section>

      {/* ── 16. Closing CTA ── */}
      <FemaleHormonesClosingCta />
    </>
  )
}
